import type * as React from 'react'

import { Button, CoachMark, NewBadge, Flex } from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import type { DismissableBanner } from '~publish/hooks/useDismissableBanner'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'

import { useSelectedChannel } from './ChannelContext'

import styles from './PostingGoalCoachMark.module.css'

import type { JSX } from 'react'

const POSTING_GOALS_LAUNCH_DATE = new Date('2025-03-25')

export const usePostingGoalCoachMark = (): DismissableBanner => {
  return useDismissableBanner('posting-goal-setup-coach-mark', {
    trackDismiss: true,
    accountSignUpBefore: POSTING_GOALS_LAUNCH_DATE,
  })
}

/**
 * Coach mark component that highlights the "Set a posting goal" feature
 * for users who haven't set a posting goal yet.
 */
export const PostingGoalCoachMark = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const channel = useSelectedChannel()
  const coachMarkBanner = usePostingGoalCoachMark()
  const openGlobalModal = useOpenGlobalModal()

  // Only show the coach mark if the channel doesn't have a posting goal
  // and the coachMarkBanner hasn't been dismissed
  const shouldShowCoachMark = coachMarkBanner.isActive && !channel.postingGoal

  const handleSetPostingGoal = (): void => {
    openGlobalModal(MODALS.postingGoalConfiguration, {
      channelId: channel.id,
    })
  }

  if (!shouldShowCoachMark) {
    return <>{children}</>
  }

  return (
    <CoachMark onDismiss={coachMarkBanner.dismiss}>
      <CoachMark.Overlay style={{ isolation: 'isolate' }}>
        <CoachMark.Spotlight
          size="small"
          style={{
            '--spotlight-top': '50%',
            '--spotlight-left': '0.32em',
            zIndex: -1,
          }}
        />
        {children}
      </CoachMark.Overlay>
      <CoachMark.Content
        side="right"
        align="start"
        showCloseIcon={false}
        className={styles.content}
      >
        <Flex gap="xs" align="center">
          <NewBadge />
          <CoachMark.Title>Stay Consistent with a Posting Goal</CoachMark.Title>
        </Flex>
        <CoachMark.Description>
          We&apos;ll recommend goals based on Buffer data and best practices, to
          help you achieve your goal!
        </CoachMark.Description>
        <CoachMark.Footer className={styles.footer}>
          <CoachMark.Dismiss>
            <Button variant="tertiary">No, Thanks</Button>
          </CoachMark.Dismiss>
          <CoachMark.Dismiss>
            <Button onClick={handleSetPostingGoal}>Set Posting Goal</Button>
          </CoachMark.Dismiss>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
